import React, { useState, useEffect } from 'react';
import { MessageCircle, Users, ArrowLeft, RefreshCw, Trash2, CheckCircle, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LoadingState } from '@/components/ui/loading-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/contexts/unified-permissions';
import { useChatParticipants } from '../hooks/useChatParticipants';
import { useChatOperations } from '../hooks/useChatOperations';
import { ChatInterface } from './ChatInterface';
import { UserPicker } from './UserPicker';
import type { ChatParticipant } from '../types';
import { formatDistanceToNow } from 'date-fns';

interface AdminChatInterfaceProps {
  className?: string;
  height?: string;
}

/**
 * Admin chat interface for the FAB dialog
 * Shows list of conversations and allows chatting with individual users
 */
export const AdminChatInterface: React.FC<AdminChatInterfaceProps> = ({
  className,
  height = 'h-[400px]',
}) => {
  const { user, isAdmin } = usePermissions();
  const { participants, loading, error, refetch } = useChatParticipants();
  const { markMessagesAsRead, deleteConversation, clearConversation, loading: operationsLoading } = useChatOperations();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Auto-mark messages as read when a conversation is selected
  useEffect(() => {
    if (selectedUserId && user?.id && isAdmin) {
      // Mark messages from the selected user as read
      markMessagesAsRead(selectedUserId);
    }
  }, [selectedUserId, user?.id, isAdmin, markMessagesAsRead]);

  // Only show for admin users
  if (!user || !isAdmin) {
    return (
      <Alert>
        <AlertDescription>
          Access denied. This interface is only available to administrators.
        </AlertDescription>
      </Alert>
    );
  }

  const selectedUser = participants.find(p => p.id === selectedUserId);

  // Handle conversation management actions
  const handleDeleteConversation = async (otherUserId: string) => {
    const success = await deleteConversation(otherUserId);
    if (success) {
      setSelectedUserId(null); // Go back to conversation list
      // Small delay to ensure database operation completes before refresh
      setTimeout(() => {
        refetch(); // Refresh the participants list
      }, 500);
    }
  };

  const handleClearConversation = async (otherUserId: string) => {
    const success = await clearConversation(otherUserId);
    if (success) {
      // Small delay to ensure database operation completes before refresh
      setTimeout(() => {
        refetch(); // Refresh the participants list to update badge counts
      }, 500);
    }
  };

  // If a user is selected, show the chat interface
  if (selectedUserId && selectedUser) {
    return (
      <div className={cn('flex flex-col', height, className)}>
        {/* Header with back button and conversation actions */}
        <div className="flex items-center gap-2 p-3 border-b bg-muted/50 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedUserId(null)}
            className="h-8 w-8 p-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <MessageCircle className="h-4 w-4" />
          <span className="font-medium flex-1">
            Chat with {selectedUser.fullName || selectedUser.username || 'User'}
          </span>

          {/* Conversation actions dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={operationsLoading}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => handleClearConversation(selectedUserId)}
                disabled={operationsLoading}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Messages
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDeleteConversation(selectedUserId)}
                disabled={operationsLoading}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Remove from List
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Chat interface */}
        <div className="flex-1 min-h-0">
          <ChatInterface
            otherUserId={selectedUserId}
            otherUserName={selectedUser.fullName || selectedUser.username || 'User'}
            className="border-0 rounded-none"
            height="h-full"
          />
        </div>
      </div>
    );
  }

  // Show conversation list
  return (
    <div className={cn('flex flex-col border rounded-lg bg-background', height, className)}>
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b bg-muted/50 flex-shrink-0">
        <MessageCircle className="h-4 w-4" />
        <span className="font-medium">Admin Chat</span>
        <Badge variant="secondary" className="ml-2">
          {participants.length} conversations
        </Badge>
        <div className="ml-auto flex items-center gap-2">
          <UserPicker onUserSelect={setSelectedUserId} />
          <Button
            variant="ghost"
            size="sm"
            onClick={refetch}
            disabled={loading}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0">
        {/* Error state */}
        {error && (
          <Alert variant="destructive" className="m-4">
            <AlertDescription>
              Failed to load conversations: {error.message}
            </AlertDescription>
          </Alert>
        )}

        {/* Loading state */}
        {loading && !participants.length && (
          <div className="flex-1 flex items-center justify-center">
            <LoadingState description="Loading conversations..." />
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && participants.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No conversations yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Users who send messages will appear here. You can then chat with them directly.
            </p>
            <p className="text-xs text-muted-foreground">
              Debug: Admin ID: {user?.id}
            </p>
          </div>
        )}

        {/* Participants list */}
        {participants.length > 0 && (
          <ScrollArea className="h-full">
            <div className="p-2">
              {participants.map((participant) => (
                <ParticipantCard
                  key={participant.id}
                  participant={participant}
                  onSelect={setSelectedUserId}
                  onClear={handleClearConversation}
                  onDelete={handleDeleteConversation}
                  operationsLoading={operationsLoading}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};

/**
 * Individual participant card component for the admin interface
 */
interface ParticipantCardProps {
  participant: ChatParticipant;
  onSelect: (userId: string) => void;
  onClear?: (userId: string) => void;
  onDelete?: (userId: string) => void;
  operationsLoading?: boolean;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({
  participant,
  onSelect,
  onClear,
  onDelete,
  operationsLoading = false
}) => {
  const displayName = participant.fullName || participant.username || 'Unknown User';
  const hasUnread = participant.unreadCount > 0;

  const handleClick = () => {
    onSelect(participant.id);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.(participant.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(participant.id);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors mb-2 group',
        'hover:bg-muted/50',
        hasUnread && 'bg-muted/30 border-primary/20'
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Avatar placeholder */}
        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-medium text-primary">
            {displayName.charAt(0).toUpperCase()}
          </span>
        </div>

        {/* User info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium truncate text-sm">{displayName}</p>
            {hasUnread && (
              <Badge variant="destructive" className="h-4 min-w-[16px] text-xs px-1">
                {participant.unreadCount > 99 ? '99+' : participant.unreadCount}
              </Badge>
            )}
          </div>
          {participant.lastActivity && (
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(participant.lastActivity, { addSuffix: true })}
            </p>
          )}
        </div>
      </div>

      {/* Action buttons - show on hover */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onClear && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={operationsLoading}
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
            title="Clear messages"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
        {onDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            disabled={operationsLoading}
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
            title="Remove from list"
          >
            <CheckCircle className="h-3 w-3" />
          </Button>
        )}
        <MessageCircle className="h-3 w-3 text-muted-foreground flex-shrink-0 ml-1" />
      </div>
    </div>
  );
};
